import React from 'react';

import PropTypes from 'prop-types';

import { TableContainer } from 'apps/auth/components/common/components/TableRow/styles';
import { DiskButton } from 'apps/auth/components/common/DiskButton/DiskButton';
import { Text } from 'apps/auth/components/common/Text/Text';
import { PAYSEND_PLUS } from 'apps/auth/components/paysendPlus/utils';
import { FEE_INFO_CLICKED } from 'apps/auth/constants/amplitude/AmplitudeEvents';
import { USA } from 'apps/auth/constants/WellKnownCountries';
import { getTableTextColor, getTextPlusSubscriptionColor } from 'apps/auth/helpers/historyUtils';
import { toLowerCase } from 'apps/auth/helpers/utils';
import { useAuth, useExtra, usePageMessages } from 'apps/auth/hooks/selectors';
import { CheckService } from 'apps/auth/services/CheckService';

import FeeTextPopup from '../FeeTextPopup';
import PreCheckDeliveryRow from './PreCheckDeliveryRow';
import PreCheckRow from './PreCheckRow';
import UnitellerConfirmationPopup from './UnitellerConfirmationPopup';

const UnitellerConfirmationDetails = (props) => {
    const {
        precheckMobileNumber,
        precheckTransferAmount,
        precheckTransferFee,
        precheckExchangeRate,
        precheckTotalToRecipient,
        unitellerPickUpGuid,
        shouldArrive,
        deliveryOption,
        precheckCashPickup,
        precheckCashPickupLocation,
        gotIt,
        gotItButton,
        unitellerPickUpGuidWorkingHours,
        unitellerCashPickupGuideButton,
        paymentOptionText,
        amountTotalBlock,
        ...messages
    } = usePageMessages();
    const { countryId: userCountry } = useAuth();
    const { precheck, payment_form: paymentForm, toSourceName } = useExtra();

    const textDelivery = toSourceName || precheck?.toBankName;

    return (
        <TableContainer indent="16px">
            {userCountry === USA && (
                <PreCheckRow
                    title={amountTotalBlock}
                    text={
                        <>
                            {precheck.amountFrom}
                            <Text size="secondary" smallCaps>
                                {' ' + toLowerCase(precheck.amountFromCurrency)}
                            </Text>
                        </>
                    }
                />
            )}
            <PreCheckRow
                onClick={() => {
                    if (precheck.commissionInfo) {
                        props.sendEvent?.(FEE_INFO_CLICKED);
                        props.popupRef?.current?.open(
                            <FeeTextPopup
                                popupRef={props.popupRef}
                                text={precheck.commissionInfo}
                                isComment={true}
                                isShowTerms={false}
                                closeHandler={props.closeHandlerForPopup}
                                subscriptionType={precheck.subscriptionType}
                                onPopupClose={props.closeHandlerForPopup}
                            />,
                        );
                    }
                }}
                title={precheckTransferFee}
                info={precheck.commissionInfo}
                text={
                    precheck.commissionInfo ? (
                        <>
                            <Text
                                size="secondary"
                                color={precheck.subscriptionType === PAYSEND_PLUS ? getTextPlusSubscriptionColor(precheck) : getTableTextColor(precheck)}
                            >
                                {(userCountry === USA && precheck.amountFrom !== precheck.amountFromWithoutFee ? ' -' : '') + precheck.commission}
                            </Text>
                            <DiskButton
                                type="button"
                                iconColor={precheck.subscriptionType === PAYSEND_PLUS ? getTextPlusSubscriptionColor(precheck) : getTableTextColor(precheck)}
                                iconName="info-rounded"
                                size="small"
                                marginRight="-8px"
                                marginTop="-4px"
                                raised={false}
                            />
                        </>
                    ) : (
                        <>
                            {' '}
                            {userCountry === USA && precheck.amountFrom !== precheck.amountFromWithoutFee ? ' -' : ''}
                            {precheck.commission}{' '}
                        </>
                    )
                }
            />
            {userCountry === USA && (
                <PreCheckRow
                    title={precheckTransferAmount}
                    text={
                        <>
                            {precheck.amountFromWithoutFee}
                            <Text size="secondary" smallCaps>
                                {' ' + toLowerCase(precheck.amountFromCurrency)}
                            </Text>
                        </>
                    }
                />
            )}
            {userCountry === USA && (
                <PreCheckRow
                    title={precheckTotalToRecipient}
                    text={
                        <>
                            {precheck.amountTo}
                            <Text size="secondary" smallCaps>
                                {' ' + toLowerCase(precheck.amountToCurrency)}
                            </Text>
                        </>
                    }
                />
            )}
            <PreCheckRow
                title={precheckExchangeRate}
                text={
                    <>
                        {precheck.rateInfo.fromAmount}
                        <Text size="secondary" smallCaps>
                            {' ' + toLowerCase(precheck.rateInfo.fromCurrency)}
                        </Text>
                        {' = '}
                        {precheck.rateInfo.toAmount}
                        <Text size="secondary" smallCaps>
                            {' ' + toLowerCase(precheck.rateInfo.toCurrency)}
                        </Text>
                    </>
                }
            />
            {(precheck.toPhoneNumber || precheck.accountTo) && (
                <PreCheckRow title={precheckMobileNumber} text={`+${precheck.toPhoneNumber || precheck.accountTo}`} />
            )}
            <PreCheckRow title={deliveryOption} text={precheckCashPickup} />
            <PreCheckDeliveryRow
                popupRef={props.popupRef}
                title={shouldArrive}
                description={paymentForm.description}
                comment={paymentForm.comment}
                deliveryMethod={precheck.deliveryMethod}
                closeHandlerForPopup={props.closeHandlerForPopup}
            />
            <PreCheckRow
                title={precheckCashPickupLocation}
                text={
                    <>
                        {textDelivery}
                        <DiskButton type="button" iconName="info-rounded" size="small" marginRight="-8px" marginTop="-4px" raised={false} />
                    </>
                }
                onClick={() => {
                    props.popupRef?.current?.open(
                        <UnitellerConfirmationPopup
                            popupRef={props.popupRef}
                            modalRef={props.modalRef}
                            closeHandlerForPopup={props.closeHandlerForPopup}
                            messages={{
                                gotItButton,
                                gotIt,
                                unitellerCashPickupGuideButton,
                                unitellerPickUpGuid,
                                unitellerPickUpGuidWorkingHours,
                                textDelivery,
                            }}
                            recipientType={props.recipientType}
                            paymentMethod={props.paymentMethod}
                        />,
                    );
                }}
            />
            {precheck.accountFrom && precheck.accountFrom.length > 0 && (
                <PreCheckRow
                    title={paymentOptionText}
                    text={CheckService.getFormattedPaymentAccount(messages, precheck.paymentMethod, userCountry, precheck.accountFrom)}
                />
            )}
        </TableContainer>
    );
};

UnitellerConfirmationDetails.propTypes = {
    isLibre: PropTypes.bool.isRequired,
    formName: PropTypes.string.isRequired,
    closeHandlerForPopup: PropTypes.func.isRequired,
    popupRef: PropTypes.shape({ current: PropTypes.object }).isRequired,
    modalRef: PropTypes.shape({ current: PropTypes.object }).isRequired,
};

export default UnitellerConfirmationDetails;
